/*
 * Authors: mariushirjanu
 * Created: 01/12/2022
 */

import * as _ from 'underscore';
import { Subscription } from 'rxjs';
import { PriceCartService } from '../../cart/price-cart.service';
import { OrderEntryWebshopDataService } from './order-entry-webshop-data.service';
import { WebshopDataService } from '../../../webshop/webshop-data.service';
import { CatalogStateService } from '../../catalog/catalog-state.service';
import { OrderEntryChargesService } from '../charges/order-entry-charges.service';
import { OrderEntryPaymentService } from '../payment/order-entry-payment.service';
import { OrderCatalogService } from '../../catalog/order-catalog.service';
import { IOrderContext, IOrderResponse, ProcurementType } from '../order-entry-data.service';

import { ICountry, ICurrency, ICurrency2, INamedObject } from '../../../../../type_definitions/datatypes';
import { OrderEntryWebshopService } from './order-entry-webshop.service';
import { Injectable } from '@angular/core';
import { GaDeferredService, IGADeferred } from '../../../../shared/services/ga-deferred.service';
import { GaEventService, IGAEvent } from '../../../../shared/services/ga-event.service';

import { EVENT_ORDER_BP_SELECTION_UPDATED, EVENT_ORDER_COUNTRY_SELECTION_UPDATED, EVENT_ORDER_WEBSHOP_SELECTION_UPDATED } from '../../../../shared/app-events';
import { OrderEntryService } from '../order-entry.service';
import { HttpError } from '../../../../shared/http/http-data.service';

export interface IWebshopLoader {
  single:boolean;
  order:IOrderResponse;
  session?:string;
}

@Injectable()
export class OrderWebshopLoaderService {
  private workflowType:string;
  private punchoutMessageId?:number;
  private order:IOrderResponse;
  private stockOrderType:string;
  private initialLoad:boolean;
  private deferred:IGADeferred<IWebshopLoader>;
  private eventHandler:Subscription;

  constructor(
    private orderCatalogService:OrderCatalogService,
    private catalogStateService:CatalogStateService,
    private deferredService:GaDeferredService,
    private gaEventService:GaEventService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private orderEntryWebshopDataService:OrderEntryWebshopDataService,
    private orderEntryPaymentService:OrderEntryPaymentService,
    private orderEntryChargesService:OrderEntryChargesService,
    private orderEntryService:OrderEntryService,
    private priceCartService:PriceCartService,
    private webshopDataService:WebshopDataService
  ) {
    /***/
  }

  public start(workflowType:string, punchoutMessageId?:number, order?:IOrderResponse, stockOrderType?:string):Promise<IWebshopLoader> {
    this.workflowType = workflowType;
    this.punchoutMessageId = punchoutMessageId;
    this.order = order;
    this.stockOrderType = stockOrderType;
    this.initialLoad = true;
    this.deferred = this.deferredService.defer();

    this.orderEntryWebshopService.clear();
    this.orderEntryWebshopService.workflowType = workflowType;
    this.orderEntryService.stockOrderTypeId = stockOrderType;

    if (!order) {
      this.loadWebshops();
      this.listen();
    } else {
      this.selectedWebshop = order.webshop;
      this.selectedCountry = order.country;
      this.selectedBP = {
        name: order.bp.location.companyCountry.company.name,
        id: order.bp.location.companyCountry.id
      };
      // the loaded order will contain all the shopContext details
      this.handleShopContext(order.context, order);

      // signal to the caller that it can proceed,
      // probably list of BPs will still be loading, but we do have a selection
      this.shopContextLoaded();
    }

    return this.deferred.promise;
  }

  private listen = () => {
    this.eventHandler = this.gaEventService.events
      .subscribe((event:IGAEvent) => {
      // Selected Shop
      if (event.name === EVENT_ORDER_WEBSHOP_SELECTION_UPDATED) {
        this.selectedWebshop = event.data;
        if(!this.order){
          this.loadCountries();
        }
      }

      // Selected Country
      if (event.name === EVENT_ORDER_COUNTRY_SELECTION_UPDATED) {
        this.selectedCountry = event.data;
        if(!this.order) {
          this.loadBPs();
        }
      }

      // Selected BP
      if (event.name === EVENT_ORDER_BP_SELECTION_UPDATED) {
        this.loadShopContext();
      }
    });
  };

  private loadWebshops = ():void => {
    this.webshopDataService.webshops(this.workflowType, this.stockOrderType)
      .then((shops:INamedObject[]) => {
        this.orderEntryWebshopService.possibleShops = shops;
        if (shops?.length === 1) {
          this.orderEntryWebshopService.selectedShop = shops[0];
        } else {
          this.loadingDone();
        }
      })
      .catch(this.loadingDone);
  };

  private loadCountries = ():void => {
    if (this.orderEntryWebshopService.selectedShop) {
      this.orderEntryWebshopService.loadingCountries = true;
      this.orderEntryWebshopDataService.countries(this.orderEntryWebshopService.selectedShop.id, this.workflowType, this.punchoutMessageId)
        .then((countries:ICountry[]) => {
          this.orderEntryWebshopService.possibleCountries = countries;
          if (countries?.length === 1) {
            this.orderEntryWebshopService.selectedCountry = countries[0];
          } else {
            this.loadingDone();
          }
        })
        .catch(this.loadingDone)
        .finally(() => {
          this.orderEntryWebshopService.loadingCountries = false;
        });
    }
  };

  private loadBPs():void {
    if (this.orderEntryWebshopService.selectedCountry) {
      this.orderEntryWebshopService.loadingBPs = true;
      this.orderEntryWebshopDataService.bps(this.orderEntryWebshopService.selectedShop.id, this.orderEntryWebshopService.selectedCountry.id, this.punchoutMessageId, this.workflowType)
        .then((bps:INamedObject[]) => {
          this.orderEntryWebshopService.possibleBPs = bps;
          if (bps?.length === 1) {
            this.selectedBP = bps[0];
          } else {
            this.loadingDone();
          }
        })
        .catch(() => {
          this.loadingDone();
        })
        .finally(() => {
          this.orderEntryWebshopService.loadingBPs = false;
        });

    }
  }

  private loadShopContext():void {
    if(this.orderEntryWebshopService.selectedCountry?.id && this.orderEntryWebshopService.selectedBP?.id) {
      this.orderEntryWebshopService.loadingContext = true;
      this.orderEntryWebshopDataService.shopContext(this.orderEntryWebshopService.selectedShop.id, this.orderEntryWebshopService.selectedCountry.id, this.orderEntryWebshopService.selectedBP.id, this.punchoutMessageId)
        .then((context:IOrderContext) => {
          this.handleShopContext(context);
          this.shopContextLoaded();
        })
        .catch((error:number) => {
          // do not mark as loading done when the promise is cancelled because another promise has started (reselection)
          if(error !== HttpError.REQUEST_CANCELLED){
            this.shopContextLoaded();
          }
        });
    } else {
      this.orderEntryWebshopService.shopContext = undefined;
    }
  }

  private shopContextLoaded():void {
    this.orderEntryWebshopService.loadingContext = false;
    this.loadingDone();
  }

  private handleShopContext(shopContext:IOrderContext, order?:IOrderResponse):void {
    if (order) { //copy stuff from order to context overriding the defaults
      shopContext.isTestOrder = order.isTestOrder;
      shopContext.billingType = order.billingType;
      shopContext.paymentType = order?.payment?.paymentType?.id || shopContext.paymentType;
      shopContext.stockOrderType = order.stockOrderType;
    }

    this.orderEntryWebshopService.shopContext = shopContext;
    this.orderEntryWebshopService.testMode = this.orderEntryWebshopService.testMode || shopContext.isTestOrder;

    if (shopContext.stockOrderType?.id) {
      this.orderEntryService.stockOrderTypeId = shopContext.stockOrderType?.id;
    }

    this.orderEntryService.requesterRequired = shopContext.requesterAllowed;

    const orderCurrency:ICurrency|ICurrency2 = order?.orderCurrency || shopContext.defaultOrderCurrency;
    this.priceCartService.currency = orderCurrency.isoCode;

    this.orderCatalogService.billType = shopContext.billingType;
    this.orderCatalogService.workflowType = this.orderEntryWebshopService.workflowType;
    this.orderCatalogService.currency = orderCurrency.isoCode;
    this.orderCatalogService.showStock = shopContext.showStock;
    this.orderCatalogService.showOnlyInStockItems = shopContext.showOnlyInStockItems;
    this.orderCatalogService.layout = this.convertLayout(shopContext.catalogLayout);
    this.orderCatalogService.showProposeAlternatives = shopContext.showProposeAlternatives;
    this.orderCatalogService.sort = shopContext.catalogSort;
    this.orderCatalogService.hidePrices = shopContext.hidePrices;
    this.orderCatalogService.isLeasingWebshop = shopContext.defaultProcurementType === ProcurementType.LEASE;
    this.orderCatalogService.start();

    this.orderEntryPaymentService.fromContext(shopContext);

    const taxPercentage:number = _.isUndefined(order?.taxPercentage)? shopContext.defaultTaxInfo.taxPercentage : order.taxPercentage;
    this.orderEntryChargesService.taxPercentage = taxPercentage * 100;
    this.orderEntryChargesService.originalTaxPercentage(taxPercentage * 100);

    this.catalogStateService.buyingGuideId = shopContext.buyingGuide?.id;
    this.catalogStateService.allowMasterStockOrders = shopContext.allowMasterStockOrders;
  }

  private convertLayout(layout:string):string {
    if (!layout) {
      return undefined;
    }
    if (layout === 'GRID1' || layout === 'GA2_TL') {
      return 'tab-light';
    }
    if (layout === 'GRID0' || layout === 'GA2_TB') {
      return 'tab-basic';
    }
    return 'block';
  }

  private loadingDone = ():void => {
    if (this.initialLoad) {
      const singleSelection = !!(this.orderEntryWebshopService.selectedShop && this.orderEntryWebshopService.selectedCountry && this.orderEntryWebshopService.selectedBP);
      this.deferred.resolve({
        single: singleSelection,
        order: this.order
      });
    }
    this.initialLoad = false;
  };


  private set selectedWebshop(webshop:INamedObject) {
    this.orderEntryWebshopService.selectedShop = webshop;
    this.orderCatalogService.webshopId = webshop? webshop.id : undefined;
    this.updateTitle();
  }

  private set selectedCountry(country:ICountry) {
    this.orderEntryWebshopService.selectedCountry = country;
    this.orderCatalogService.countryId = country? country.id : undefined;
    this.updateTitle();
  }

  private set selectedBP(bp:INamedObject) {
    this.orderEntryWebshopService.selectedBP = bp;
    this.orderCatalogService.companyCountryId = bp? bp.id : undefined;
  }

  private updateTitle():void {
    if (this.orderEntryWebshopService.selectedShop && this.orderEntryWebshopService.selectedCountry) {
      this.orderEntryWebshopService.title = `${this.orderEntryWebshopService.selectedShop.name} - ${this.orderEntryWebshopService.selectedCountry.name}`;
    } else {
      this.orderEntryWebshopService.title = '';
    }
  }

  public clear():void {
    this.orderEntryWebshopService.clear();
    this.eventHandler?.unsubscribe();
  }
}